"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Link } from "@/components/common/Link";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
export default function ParentEducation() {
  const {
    ref: headingRef,
    isVisible: headingVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: contentRef,
    isVisible: contentVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: imageRef,
    isVisible: imageVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const cityPlannerPrinciples = [{
    number: "01",
    title: "Child-centered focus",
    description: "We prioritize the emotional and developmental needs of children, ensuring every interaction remains neutral and safe."
  }, {
    number: "02",
    title: "Trauma-informed approach",
    description: "Our staff is trained to recognize and mitigate the impact of high-conflict situations on family members."
  }, {
    number: "03",
    title: "Neutral observation",
    description: "We provide objective, factual reporting for the court, maintaining strict neutrality in all parenting time matters."
  }, {
    number: "04",
    title: "Structured environment",
    description: "Clear boundaries and consistent protocols create a predictable space for families to navigate complex transitions."
  }, {
    number: "05",
    title: "Professional accountability",
    description: "We adhere to rigorous standards of practice, ensuring transparency and reliability for all referral partners."
  }];
  const privateClientPrinciples = [{
    number: "01",
    title: "Compassionate guidance",
    description: "We offer supportive education to help parents develop healthy communication and conflict-resolution skills."
  }, {
    number: "02",
    title: "Skill-building workshops",
    description: "Practical sessions focused on co-parenting strategies that prioritize the well-being of your children."
  }, {
    number: "03",
    title: "Safe communication tools",
    description: "Learn effective techniques to manage high-conflict interactions and maintain a child-focused perspective."
  }, {
    number: "04",
    title: "Reunification support",
    description: "Tailored programs designed to assist families in rebuilding trust and connection in a controlled, safe setting."
  }, {
    number: "05",
    title: "Ongoing family resources",
    description: "We provide continuous support and educational materials to help your family thrive through every stage of growth."
  }];
  return <section data-section-id="5069" id="parent-education" className="bg-background text-foreground py-40 overflow-hidden">
      <div className="mx-auto max-w-[1400px] px-0 lg:px-0">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 lg:gap-0 items-stretch">
          {}
          <div ref={imageRef} className={`relative min-h-[420px] lg:min-h-[720px] transition-all duration-1000 ease-out ${imageVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"}`}>
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/family_support_services.png" alt="Professional family support services in a calm, neutral environment" loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover" />
          </div>

          {}
          <div className="px-6 sm:px-10 lg:px-16 xl:px-20 py-16 lg:py-24 flex flex-col justify-center">
            {}
            <div ref={headingRef} className={`transition-all duration-1000 ease-out ${headingVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
              <p className="text-xs sm:text-sm font-sans uppercase tracking-[0.25em] mb-8 text-secondary">
                — Education & Support
              </p>

              <h2 className="font-default text-5xl sm:text-6xl lg:text-7xl xl:text-[5.5rem] leading-[1.02] font-light text-foreground">
                Family{" "}
                <span className="italic font-light text-secondary">
                  Growth
                </span>
              </h2>

              <div className="mt-10 h-px w-24 bg-secondary" />

              <p className="mt-10 font-default text-lg sm:text-xl leading-relaxed text-foreground/85" style={{
              maxWidth: "42ch"
            }}>Utilizing the evidence-based Nurturing Parenting Programs® model, we provide family-centered, trauma-informed support focused on essential skill-building. Our comprehensive parenting education is integrated into professional service delivery to help families navigate complex transitions with resilience and clarity.</p>
            </div>

            {}
            <div ref={contentRef} className={`mt-14 transition-all duration-1000 delay-150 ease-out ${contentVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
              <Tabs defaultValue="planners" className="w-full">
                <TabsList className="bg-transparent p-0 h-auto gap-3 flex flex-wrap">
                  <TabsTrigger value="planners" className="font-default italic text-base sm:text-lg px-6 py-2.5 rounded-md border border-secondary text-foreground bg-transparent data-[state=active]:bg-secondary data-[state=active]:text-white data-[state=active]:border-secondary transition-colors">
                    For Professionals
                  </TabsTrigger>
                  <TabsTrigger value="private" className="font-default italic text-base sm:text-lg px-6 py-2.5 rounded-md border border-secondary text-foreground bg-transparent data-[state=active]:bg-secondary data-[state=active]:text-white data-[state=active]:border-secondary transition-colors">
                    For Families
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="planners" className="mt-10 data-[state=active]:animate-in data-[state=active]:fade-in-0 data-[state=active]:duration-500">
                  <ul className="space-y-8">
                    {cityPlannerPrinciples.map((item, i) => <li data-index={i} key={item.number} className="flex gap-5">
                        <span className="font-sans text-sm font-medium tracking-wider pt-1.5 shrink-0 text-secondary">
                          {item.number}
                        </span>
                        <div className="flex-1">
                          <h3 className="font-default italic text-xl sm:text-2xl font-light text-foreground leading-snug">
                            {item.title}
                          </h3>
                          <p className="mt-2 font-default text-base leading-relaxed text-foreground/75 max-w-[48ch]">
                            {item.description}
                          </p>
                        </div>
                      </li>)}
                  </ul>
                </TabsContent>

                <TabsContent value="private" className="mt-10 data-[state=active]:animate-in data-[state=active]:fade-in-0 data-[state=active]:duration-500">
                  <ul className="space-y-8">
                    {privateClientPrinciples.map((item, i) => <li data-index={i} key={item.number} className="flex gap-5">
                        <span className="font-sans text-sm font-medium tracking-wider pt-1.5 shrink-0 text-secondary">
                          {item.number}
                        </span>
                        <div className="flex-1">
                          <h3 className="font-default italic text-xl sm:text-2xl font-light text-foreground leading-snug">
                            {item.title}
                          </h3>
                          <p className="mt-2 font-default text-base leading-relaxed text-foreground/75 max-w-[48ch]">
                            {item.description}
                          </p>
                        </div>
                      </li>)}
                  </ul>
                </TabsContent>
              </Tabs>

              {}
              <p className="mt-14 font-default italic text-base sm:text-lg text-foreground/85">
                <Link to="/ContactPage" className="underline underline-offset-[6px] decoration-1 transition-colors hover:opacity-80 text-secondary">
                  Inquire about our family support services
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}